
import { computed, defineComponent, getCurrentInstance } from 'vue';
import DocIcon from '@/components/ui/icons/DocIcon.vue';
import type { GtmEvent } from '@/plugins/gdprPlugin';

type ContentLang = 'ru' | 'en' | 'kz';

interface FooterContent {
  copyrightTitle: string;
  copyrightAddress: string;
  needHelp: string;
  haveQuestions: string;
  techSupport: string;
  findUs: string;
  voximplantTag: string;
  forAnswers: string;
}

const CONTENT_BY_LANG: Record<ContentLang, (year: number) => FooterContent> = {
  ru: (currentYear) => ({
    copyrightTitle: `© ${currentYear} Voximplant®. Все права защищены.`,
    copyrightAddress: 'ООО Фастком Москва, Мытная 66, 2й этаж, 115191',
    needHelp: 'Нужна помощь?',
    haveQuestions: 'В случае возникновения вопросов, обращайтесь в нашу ',
    techSupport: 'службу поддержи',
    findUs: ' или ищите нас по ',
    voximplantTag: 'тегу Voximplant',
    forAnswers: ' на Stack Overflow для получения ответов на общие вопросы.',
  }),
  kz: (currentYear) => ({
    copyrightTitle: `© ${currentYear} Voximplant®. Все права защищены.`,
    copyrightAddress:
      'ТОО «Voximplant KZ (Воксимплант КЗ)», город Астана, улица Тарас Шевченко, здание 4/1, н.п. 17, 010000',
    needHelp: 'Нужна помощь?',
    haveQuestions: 'В случае возникновения вопросов, обращайтесь в нашу ',
    techSupport: 'службу поддержи',
    findUs: ' или ищите нас по ',
    voximplantTag: 'тегу Voximplant',
    forAnswers: ' на Stack Overflow для получения ответов на общие вопросы.',
  }),
  en: (currentYear) => ({
    copyrightTitle: `© ${currentYear} Voximplant® All rights reserved.`,
    copyrightAddress:
      '150 West 25th Street, RM 403 New York City, NY 10001, United States of America',
    needHelp: 'Need some help?',
    haveQuestions: 'Feel free to reach our ',
    techSupport: 'support team',
    findUs: ' with any questions, or search for the ',
    voximplantTag: 'Voximplant tag',
    forAnswers: ' on Stack Overflow for common questions and answers.',
  }),
};

export default defineComponent({
  name: 'Footer',
  components: {
    DocIcon,
  },
  setup() {
    const instance = getCurrentInstance();
    const proxy = instance?.proxy as
      | {
          $sendGtmEvent?: (payload: GtmEvent) => void;
          $router?: { currentRoute: { path: string } };
        }
      | undefined;

    const sendGtmEventToProxy = proxy?.$sendGtmEvent;
    const router = proxy?.$router;

    const currentYear = computed(() => new Date().getFullYear());

    const content = computed<FooterContent>(() => {
      const lang: ContentLang = (process.env.VUE_APP_DOMAIN as ContentLang) || 'en';
      return CONTENT_BY_LANG[lang](currentYear.value);
    });

    function sendGtmSupport() {
      sendGtmEventToProxy?.({
        event: 'vox_docs_ask_support',
        category: 'help',
        action: 'ask_support',
        label: router?.currentRoute?.path,
      });
    }

    function sendGtmStack() {
      sendGtmEventToProxy?.({
        event: 'vox_docs_stackoverflow_go',
        category: 'help',
        action: 'go_stackoverflow',
        label: router?.currentRoute?.path,
      });
    }

    function sendGtmLinks(link: string) {
      sendGtmEventToProxy?.({
        event: 'vox_docs_footer_to_other_pages',
        category: 'main_docs_footer',
        action: 'other_pages',
        label: link.toLowerCase(),
      });
    }

    function sendGtmSocialMedia(mediaName: string) {
      sendGtmEventToProxy?.({
        event: 'vox_docs_footer_to_social_media',
        category: 'main_docs_footer',
        action: 'social_media',
        label: mediaName.toLowerCase(),
      });
    }

    return {
      content,
      sendGtmSupport,
      sendGtmStack,
      sendGtmLinks,
      sendGtmSocialMedia,
    };
  },
});
